import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type { RootState } from '..';
import config from '../../config';

interface User {
  _id: string;
  username: string;
  email: string;
  mobile_number: string;
  role: string;
  shop_id?: string;
  is_active: boolean;
  is_blocked: boolean;
  created_at: string;
  updated_at: string;
}

interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  mobile_number: string;
  role: string;
  shop_id?: string;
}

interface UpdateUserRequest {
  username?: string;
  email?: string;
  mobile_number?: string;
  role?: string;
  shop_id?: string;
  is_active?: boolean;
  is_blocked?: boolean;
}

interface ResetPasswordRequest {
  id: string;
  new_password: string;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => ({
        url: '/users',
        method: 'GET',
        credentials: 'include',
      }),
      providesTags: ['Users'],
      transformResponse: (response: User[]) => {
        return response;
      },
    }),
    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<User, { id: string; data: UpdateUserRequest }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    resetPassword: builder.mutation<void, ResetPasswordRequest>({
      query: ({ id, new_password }) => ({
        url: `/users/${id}/reset-password`,
        method: 'POST',
        body: { password: new_password },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
} = usersApi;